// File: packages/calculator/components/SummaryPanel.tsx

import React from 'react';
import { Button } from '@dopomogai/ui';
import { DollarSign, Clock, BarChart3, Share2, Info } from 'lucide-react';

import type { QuotePayload } from '@dopomogai/types';
import { useCalculator } from '@dopomogai/agent-kit';
import { useTotals } from '@dopomogai/agent-kit';
import { useShareState } from '@dopomogai/agent-kit';
import { fmtMoney } from '@dopomogai/agent-kit';

type Props = {
  onGetQuote?: (payload: QuotePayload) => void;
  onBookCall?: () => void;
};

/**
 * A sticky right-rail summary panel showing derived totals (price, ETA, impacts)
 * and primary calls-to-action.
 */
export function SummaryPanel({ onGetQuote, onBookCall }: Props) {
  const { spec, state } = useCalculator();
  const { totals } = useTotals(spec, state);
  const { buildShareUrlFromState, copyShareUrlFromState } = useShareState(spec, state, { persist: 'url' });

  const handleCopyLink = async () => {
    try {
      await copyShareUrlFromState();
      // Optional: Add a toast notification for success
      alert('Link with your selections copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('Could not copy link. Please try again.');
    }
  };

  const handleGetQuote = () => {
    onGetQuote?.({
      platformId: state.platformId,
      selectedModuleIds: Array.from(state.selectedModuleIds),
      presetId: state.presetId,
      totals,
      currency: spec.currency,
      // Note: Full ROI details can be added here if needed
      shareUrl: buildShareUrlFromState(),
    });
  };

  return (
    <div className="sticky top-8">
      <div
        className="border-2 border-black rounded-xl bg-white p-5 transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0F172A]"
      >
        <h3 className="text-xl font-extrabold text-slate-900 mb-4">Your Estimate</h3>

        <div className="space-y-3">
          <SummaryStat
            icon={<DollarSign className="w-5 h-5" />}
            label="One-Time Setup"
            value={fmtMoney(totals.totalPriceUSD, spec.currency)}
          />
          <SummaryStat
            icon={<Clock className="w-5 h-5" />}
            label="Estimated ETA"
            value={`${totals.etaWeeks[0].toFixed(1)}–${totals.etaWeeks[1].toFixed(1)} weeks`}
          />
          <SummaryStat
            icon={<BarChart3 className="w-5 h-5" />}
            label="Projected Impact"
            value={`+${totals.convLiftPp[0].toFixed(1)}–${totals.convLiftPp[1].toFixed(1)}% CVR Lift`}
          />
        </div>

        <div className="mt-4 text-xs text-slate-600 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            {spec.socialImpactPct}% of platform revenue funds non-profit builds.
          </span>
        </div>

        <div className="mt-5 pt-5 border-t-2 border-slate-200 space-y-2">
          <Button variant="primary" size="lg" fullWidth onClick={handleGetQuote}>
            Get Quote
          </Button>
          <Button variant="secondary" fullWidth onClick={onBookCall}>
            Book a Call
          </Button>
          <Button variant="ghost" fullWidth onClick={handleCopyLink}>
            <Share2 className="w-4 h-4 mr-2" />
            Copy Link with Selections
          </Button>
        </div>
      </div>
    </div>
  );
}

// Helper sub-component for consistent stat display
function SummaryStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="border-2 border-black rounded-lg bg-slate-50 p-3 flex items-center gap-3">
      <div className="text-slate-800">{icon}</div>
      <div>
        <div className="font-extrabold text-slate-900 text-lg">{value}</div>
        <div className="text-sm font-semibold text-slate-600">{label}</div>
      </div>
    </div>
  );
}